import { call, put, select, takeEvery } from 'redux-saga/effects';

import * as authSelectors from '@redux/reducers/auth';
import API_BASE_URL from '@redux/sagas/settings/apibaseurl';
import { store } from '@redux/storeConfig/store';
import * as alerts from '@redux/utils/alerts';
import camelcaseKeys from 'camelcase-keys';
import { normalize } from 'normalizr';
import { appIntl } from '../../../../utility/context/IntlGlobalProvider';
import * as entitySelectors from '../../Entities/reducers';
import * as actions from '../actions';
import * as selectors from '../reducers';
import * as schemas from '../schemas';
import {
  getFormByIdService,
  getFormsListService,
  getFormsQuestionsListService,
  getFormsSectionsListService,
  resendResponseFormService,
  saveExternalFormService,
  saveFormService,
  saveResponseFormService
} from '../services/forms';
import * as types from '../types';

/* -------------------------------------------------------------------------- */
/*                                 FETCH LIST                                 */
/* -------------------------------------------------------------------------- */
function* fetchFormsList(action) {
  getFormsListService(
    {},
    {
      successAction: (response) => {
        const resultData = response.data.map((result) => camelcaseKeys(result));
        const {
          entities: { forms },
          result //order
        } = normalize(resultData, schemas.forms); //normalize data to byId and order
        store.dispatch(actions.completeFetchingForms(forms, result));
      },
      errorAction: (error) => {
        store.dispatch(actions.failFetchingForms(error));
      }
    }
  );
}

export function* watchFetchFormsList() {
  yield takeEvery(types.FORMS_FETCH_STARTED, fetchFormsList);
}

/* -------------------------------------------------------------------------- */
/*                                 FETCH LIST                                 */
/* -------------------------------------------------------------------------- */
function* partialFetchFormsList(action) {
  const entityTypeId = action.payload;
  const date = yield select(entitySelectors.getUserUpdateByEntityDate, entityTypeId);
  getFormsListService(
    { userFetchDate: date },
    {
      successAction: (response) => {
        const resultData = response.data.map((result) => camelcaseKeys(result));
        const {
          entities: { forms },
          result //order
        } = normalize(resultData, schemas.forms); //normalize data to byId and order
        store.dispatch(actions.completePartialFetchingForms(forms, result));
      },
      errorAction: (error) => {
        store.dispatch(actions.failPartialFetchingForms(error));
      }
    }
  );
}

export function* watchPartialFetchFormsList() {
  yield takeEvery(types.FORMS_PARTIAL_FETCH_STARTED, partialFetchFormsList);
}

/* -------------------------------------------------------------------------- */
/*                                 FETCH BY ID                                */
/* -------------------------------------------------------------------------- */
function* fetchFormById(action) {
  const formId = action.payload;
  getFormByIdService(
    { formId },
    {
      successAction: (response) => {
        const formList = response.data.formList.map((result) => camelcaseKeys(result));
        const sectionList = response.data.sectionList.map((result) => camelcaseKeys(result));
        const questionList = response.data.questionList.map((result) => camelcaseKeys(result));
        const {
          entities: { forms },
          result: resultForm //order
        } = normalize(formList, schemas.forms); //normalize data to byId and order

        const {
          entities: { sections },
          result: resultSections //order
        } = normalize(sectionList, schemas.sections);

        const {
          entities: { questions },
          result: resultQuestions //order
        } = normalize(questionList, schemas.questions);

        store.dispatch(
          actions.completeFetchingForm(
            forms,
            resultForm,
            sections,
            resultSections,
            questions,
            resultQuestions
          )
        );
      },
      errorAction: (error) => {
        store.dispatch(actions.failFetchingForm(error));
      }
    }
  );
}

export function* watchFetchFormById() {
  yield takeEvery(types.FORM_FETCH_STARTED, fetchFormById);
}

/* -------------------------------------------------------------------------- */
/*                              FETCH SECTIONS                                */
/* -------------------------------------------------------------------------- */
function* fetchSections(action) {
  const formId = action.payload;

  getFormsSectionsListService(
    { formId },
    {
      successAction: (response) => {
        const resultData = response.data.map((result) => camelcaseKeys(result));
        const {
          entities: { sections },
          result //order
        } = normalize(resultData, schemas.sections); //normalize data to byId and order
        store.dispatch(actions.completeFetchingFormSections(sections, result, formId));
      },
      errorAction: (error) => {
        store.dispatch(actions.failFetchingFormSections(error));
      }
    }
  );
}

export function* watchFetchSections() {
  yield takeEvery(types.FORM_SECTIONS_FETCH_STARTED, fetchSections);
}

/* -------------------------------------------------------------------------- */
/*                             FETCH QUESTIONS                                */
/* -------------------------------------------------------------------------- */
function* fetchQuestions(action) {
  const formId = action.payload;

  getFormsQuestionsListService(
    { formId },
    {
      successAction: (response) => {
        const resultData = response.data.map((result) => camelcaseKeys(result));
        const {
          entities: { questions },
          result //order
        } = normalize(resultData, schemas.questions); //normalize data to byId and order
        store.dispatch(actions.completeFetchingFormQuestions(questions, result, formId));
      },
      errorAction: (error) => {
        store.dispatch(actions.failFetchingFormQuestions(error));
      }
    }
  );
}

export function* watchFetchQuestions() {
  yield takeEvery(types.FORM_QUESTIONS_FETCH_STARTED, fetchQuestions);
}

/* -------------------------------------------------------------------------- */
/*                                     ADD                                    */
/* -------------------------------------------------------------------------- */
function* addForm(action) {
  const form = action.payload;
  // eslint-disable-next-line no-console
  console.log('[DEBUG][saga addForm] outgoing payload requiresTaskAssignment=', form?.requiresTaskAssignment, form);
  saveFormService(
    { ...form, organizationFormId: null },
    {
      successAction: (response) => {
        // eslint-disable-next-line no-console
        console.log('[DEBUG][saga addForm] response data =', response?.data);
        const addedForm = camelcaseKeys(response.data);
        store.dispatch(actions.completeAddingForm(form.organizationFormId, addedForm));
      },
      errorAction: (error) => {
        // eslint-disable-next-line no-console
        console.log('[DEBUG][saga addForm] error =', error);
        store.dispatch(actions.failAddingForm(form.organizationFormId, error));
      }
    }
  );
}

export function* watchAddFormsStarted() {
  yield takeEvery(types.FORM_ADD_STARTED, addForm);
}

/* -------------------------------------------------------------------------- */
/*                                    EDIT                                    */
/* -------------------------------------------------------------------------- */
function* editForm(action) {
  const form = action.payload;
  // eslint-disable-next-line no-console
  console.log('[DEBUG][saga editForm] outgoing payload requiresTaskAssignment=', form?.requiresTaskAssignment, form);
  const { oldForm } = yield select((state) => selectors.getForm(state, form.organizationFormId));
  saveFormService(
    { ...form },
    {
      successAction: (response) => {
        // eslint-disable-next-line no-console
        console.log('[DEBUG][saga editForm] response data =', response?.data);
        const editedForm = camelcaseKeys(response?.data);
        store.dispatch(actions.completeEditingForm({ ...editedForm, questions: [] }));
      },
      errorAction: (error) => {
        // eslint-disable-next-line no-console
        console.log('[DEBUG][saga editForm] error =', error);
        store.dispatch(actions.failEditingForm(oldForm, error));
      }
    }
  );
}

export function* watchEditFormsStarted() {
  yield takeEvery(types.FORM_EDIT_STARTED, editForm);
}

/* -------------------------------------------------------------------------- */
/*                                 ADD EXTERNAL                               */
/* -------------------------------------------------------------------------- */
function* addFormExternal(action) {
  const form = action.payload;
  // eslint-disable-next-line no-console
  console.log('[DEBUG][saga addFormExternal] outgoing payload requiresTaskAssignment=', form?.requiresTaskAssignment, form);
  saveExternalFormService(
    { ...form, organizationFormId: null },
    {
      successAction: (response) => {
        // eslint-disable-next-line no-console
        console.log('[DEBUG][saga addFormExternal] response data =', response?.data);
        const addedForm = camelcaseKeys(response.data);
        store.dispatch(actions.completeAddingForm(form.organizationFormId, addedForm));
      },
      errorAction: (error) => {
        // eslint-disable-next-line no-console
        console.log('[DEBUG][saga addFormExternal] error =', error);
        store.dispatch(actions.failAddingForm(form.organizationFormId, error));
      }
    }
  );
}

export function* watchAddExternalFormsStarted() {
  yield takeEvery(types.FORM_EXTERNAL_ADD_STARTED, addFormExternal);
}

/* -------------------------------------------------------------------------- */
/*                                EDIT EXTERNAL                               */
/* -------------------------------------------------------------------------- */
function* editFormExternal(action) {
  const form = action.payload;
  // eslint-disable-next-line no-console
  console.log('[DEBUG][saga editFormExternal] outgoing payload requiresTaskAssignment=', form?.requiresTaskAssignment, form);
  const { oldForm } = yield select((state) => selectors.getForm(state, form.organizationFormId));
  saveExternalFormService(
    { ...form },
    {
      successAction: (response) => {
        // eslint-disable-next-line no-console
        console.log('[DEBUG][saga editFormExternal] response data =', response?.data);
        const editedForm = camelcaseKeys(response.data);
        store.dispatch(actions.completeEditingForm({ ...editedForm, questions: [] }));
      },
      errorAction: (error) => {
        // eslint-disable-next-line no-console
        console.log('[DEBUG][saga editFormExternal] error =', error);
        store.dispatch(actions.failEditingForm(oldForm, error));
      }
    }
  );
}

export function* watchEditExternalFormsStarted() {
  yield takeEvery(types.FORM_EXTERNAL_EDIT_STARTED, editFormExternal);
}

/* -------------------------------------------------------------------------- */
/*                                   REMOVE                                   */
/* -------------------------------------------------------------------------- */
function* removeForm(action) {
  const { formId } = action.payload;
  try {
    const isAuth = yield select(authSelectors.isAuthenticated);
    if (isAuth) {
      const token = yield select(authSelectors.getAuthToken);
      const deletedForm = yield select((state) => selectors.getForm(state, formId));
      const response = yield call(fetch, `${API_BASE_URL}/form/delete`, {
        method: 'POST',
        body: JSON.stringify({ formId }),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
      const jsonResult = yield response.json();
      if (response.status <= 300 && jsonResult.success) {
        yield put(actions.completeRemovingForm(formId));
        alerts.showSuccessDeletedMessage({
          object: appIntl().formatMessage({
            id: 'forms.form'
          }),
          name: deletedForm.organizationFormName
        });
      } else {
        const error = {
          errorMessage: jsonResult.message,
          errorNumber: jsonResult.number
        };
        // alerts.showErrorAlertFailChanges({ error });
        yield put(actions.failRemovingForm(formId, error));
      }
    }
  } catch (err) {
    const error = {
      errorMessage: err.toString(),
      errorNumber: -1
    };
    // alerts.showErrorAlertFailChanges({ error });
    yield put(actions.failRemovingForm(formId, error));
  }
}

export function* watchRemoveFormStarted() {
  yield takeEvery(types.FORM_REMOVE_STARTED, removeForm);
}

/* -------------------------------------------------------------------------- */
/*                              Save responses                               */
/* -------------------------------------------------------------------------- */
function* saveResponse(action) {
  const formResponse = action.payload;
  saveResponseFormService(
    { ...formResponse },
    {
      successAction: (response) => {
        store.dispatch(actions.completeAddingResponse());
      },
      errorAction: (error) => {
        store.dispatch(actions.failAddingResponse());
      }
    }
  );
}

export function* watchResponseAddedStarted() {
  yield takeEvery(types.FORM_RESPONSE_SAVE_STARTED, saveResponse);
}

/* -------------------------------------------------------------------------- */
/*                             Resend responses                               */
/* -------------------------------------------------------------------------- */
function* resendResponse(action) {
  const { organizationFormId, responseIds } = action.payload;
  resendResponseFormService(
    { organizationFormId, responseIds },
    {
      successAction: (response) => {
        store.dispatch(actions.completeResendingResponse(organizationFormId, responseIds));
      },
      errorAction: (error) => {
        store.dispatch(actions.failResendingResponse(organizationFormId, responseIds));
      }
    }
  );
}

export function* watchResponseResendStarted() {
  yield takeEvery(types.FORM_RESPONSE_RESEND_STARTED, resendResponse);
}
